"use client";

import { datadogLogs } from "@datadog/browser-logs";
import { SENSITIVE_KEYS } from "@/datadog/config";
import { getStepLogLabel } from "@/types/routes";
import { readStepCookie } from "@/utils/read-step-cookie";

type LogContext = Record<string, unknown>;

/** `DatadogInit` only starts the SDK where telemetry is on and consent given. */
const isDatadogActive = () => datadogLogs.getInitConfiguration() !== undefined;

const redact = (value: unknown): unknown => {
	if (value === null || typeof value !== "object") return value;

	if (Array.isArray(value)) return value.map(redact);

	return Object.entries(value).reduce(
		(acc, [key, entry]) => {
			acc[key] = SENSITIVE_KEYS.includes(key)
				? "[REDACTED]"
				: redact(entry);
			return acc;
		},
		{} as Record<string, unknown>
	);
};

/** Tags every browser log with the wizard step the customer is currently on. */
const withStep = (context?: LogContext): LogContext => ({
	step: getStepLogLabel(readStepCookie()),
	...context,
});

const sanitize = (context?: LogContext): LogContext =>
	(redact(withStep(context)) as LogContext) ?? {};

export const logInfo = (message: string, context?: LogContext) => {
	if (!isDatadogActive()) return;

	datadogLogs.logger.info(message, sanitize(context));
};

export const logWarn = (message: string, context?: LogContext) => {
	if (!isDatadogActive()) return;

	datadogLogs.logger.warn(message, sanitize(context));
};

export const logError = (
	message: string,
	error?: unknown,
	context?: LogContext
) => {
	const normalizedError =
		error instanceof Error
			? error
			: error
				? new Error(String(error))
				: undefined;

	if (!isDatadogActive()) return;

	datadogLogs.logger.error(message, sanitize(context), normalizedError);
};

/** Attaches the application id to every subsequent browser log. */
export const setLoggingApplicationId = (applicationId: string) => {
	if (!isDatadogActive()) return;

	datadogLogs.setGlobalContextProperty("applicationId", applicationId);
};
